import React from 'react';
import { Neo, Views, NeoGrid } from '@singularsystems/neo-react';
import { observer } from 'mobx-react';
import HoldingsVM from './HoldingsVM';
import CollapsibleCard from '../../../App/Components/CollapsibleCard';
import { TextConstants } from '../../../Common/TextConstants';
import SecurityHoldingLookup from '../Models/Lookups/SecurityHoldingLookup';
import CashHoldingLookup from '../Models/Lookups/CashHoldingLookup';
import FileDownloadComponent from '../../../Common/Components/FileDownload/FileDownloadComponent';
import { Validation } from '@singularsystems/neo-core';
import Loading from '../../../App/Components/Loading/Loading';

@observer
export default class HoldingsView extends Views.ViewBase<HoldingsVM> {
    constructor(props: unknown) {
        super("Holdings", HoldingsVM, props);
    }

    public render() {
        return (
            <div>
                {(this.viewModel.taskRunner.isBusy) && <Loading />}
                <CollapsibleCard title={TextConstants.Titles.Holdings} isExpanded={true}>
                    <Neo.Form
                        model={this.viewModel.holdingsLookupCriteria}
                        onSubmit={() => this.taskRunner.waitFor(this.viewModel.refreshPageManagers())}
                        validationDisplayMode={Validation.DisplayMode.AfterBlur}>
                        <Neo.GridLayout md={4}>
                            <Neo.FormGroup label={TextConstants.Labels.Broker}
                                bindContext={this.viewModel.holdingsLookupCriteria.meta.groupingDescription}>
                                <Neo.AutoCompleteDropDown
                                    itemSource={this.viewModel.serviceProviderApiClient.getServiceProviderFileGroupingListByCriteria}
                                    bind={this.viewModel.holdingsLookupCriteria.meta.staticServiceProviderFileGroupingID}
                                    bindDisplay={this.viewModel.holdingsLookupCriteria.meta.groupingDescription}
                                    displayMember={"groupingDescription"}
                                    items={this.viewModel.serviceProviderApiClient.getServiceProviderFileGroupingListByCriteria(" ").then((arr) => arr.data
                                    .sort((x,y) => x.groupingDescription.localeCompare(y.groupingDescription)))}
                                />
                            </Neo.FormGroup>
                            <Neo.FormGroup bindContext={this.viewModel.holdingsLookupCriteria.meta.clientAccountNumber}
                                label={TextConstants.Labels.ClientNumber}>
                                <Neo.AutoCompleteDropDown
                                    minCharacters={3}
                                    delayMs={1500}
                                    bind={this.viewModel.holdingsLookupCriteria.meta.clientAccountNumber}
                                    bindDisplay={this.viewModel.holdingsLookupCriteria.meta.clientAccountDescription}
                                    valueMember={"clientNumber"}
                                    displayMember={"clientDescription"}
                                    items={this.viewModel.filteredClientAccounts}
                                    itemSource={async (searchText) => this.viewModel.clientAccounts.filter(clientAccount => clientAccount.clientDescription.toLowerCase().includes(searchText.toLowerCase()))}
                                />
                            </Neo.FormGroup>
                            <Neo.FormGroup
                                label={TextConstants.Labels.Account}
                                bind={this.viewModel.holdingsLookupCriteria.meta.rawAccountNumber}
                            />
                            <Neo.FormGroup
                                bindContext={this.viewModel.holdingsLookupCriteria.meta.instrument}>
                                <Neo.AutoCompleteDropDown
                                    minCharacters={3}
                                    delayMs={1500}
                                    label={TextConstants.Labels.Instrument}
                                    bind={this.viewModel.holdingsLookupCriteria.meta.instrumentID}
                                    bindDisplay={this.viewModel.holdingsLookupCriteria.meta.instrument}
                                    displayMember={"description"}
                                    items={this.viewModel.filteredInstruments}
                                    itemSource={async (searchText) => this.viewModel.instruments.filter(instrument => instrument.description?.toLowerCase().includes(searchText.toLowerCase()))}
                                />
                            </Neo.FormGroup>
                        </Neo.GridLayout>
                        <div className="row">
                            <div className="col-md-3">
                                <Neo.FormGroup
                                    appendText="fa-calendar"
                                    label={TextConstants.Labels.Date}
                                    bind={this.viewModel.holdingsLookupCriteria.meta.date} />
                            </div>
                            <div className="col float-right">
                                <Neo.Button
                                    isSubmit
                                    variant="primary" className="form-btn float-right col-md-2"
                                >{TextConstants.Buttons.Search}</Neo.Button>
                            </div>
                        </div>
                    </Neo.Form>
                    {this.viewModel.securityHoldingsPageManager.totalRecords === 0 && this.viewModel.cashHoldingsPageManager.totalRecords === 0 &&
                        <div className="col-md-12 p=0 text-center">
                            {TextConstants.GeneralText.NoRecords}
                        </div>
                    }
                    {((this.viewModel.cashHoldingsPageManager.totalRecords > 0) || (this.viewModel.securityHoldingsPageManager.totalRecords > 0)) &&
                        <Neo.Card title={TextConstants.Titles.HoldingsExtract} className="vertical-scroll">
                            {((this.viewModel.securityHoldingsPageManager.totalRecords > 0) || (this.viewModel.cashHoldingsPageManager.totalRecords > 0)) &&
                                <div className="pb-5">
                                    <FileDownloadComponent
                                        viewModel={this.viewModel.fileDownloadVM}
                                        onDownloadCSV={() => this.viewModel.downloadHoldingsFile()}
                                    />
                                </div>
                            }
                            {this.viewModel.cashHoldingsPageManager.totalRecords > 0 &&
                                <Neo.Card title={TextConstants.Titles.CashHoldings}>
                                    <div>
                                        <Neo.Pager pageManager={this.viewModel.cashHoldingsPageManager} pageControlProps={{ pageSizeLabel: "Show: " }} >
                                            <div className="horizontalScroll inner-container">
                                                <NeoGrid.Grid<CashHoldingLookup> title={TextConstants.Titles.CashHoldings}>
                                                    {(item, meta) => (
                                                        <NeoGrid.Row>
                                                            <NeoGrid.Column label={TextConstants.Labels.ClientNumber} display={meta.clientAccount} />
                                                            <NeoGrid.Column label={TextConstants.Labels.Account} display={meta.rawAccountNumber} />
                                                            <NeoGrid.Column label={TextConstants.Labels.ReportDate} display={meta.reportDate} />
                                                            <NeoGrid.Column label={TextConstants.Labels.Currency} display={meta.currency} />
                                                            <NeoGrid.Column label={TextConstants.Labels.Balance} display={meta.balance} cellClassName="text-right" />
                                                            <NeoGrid.Column label={TextConstants.Labels.BalanceNC} display={meta.balanceUSD} cellClassName="text-right" />
                                                        </NeoGrid.Row>
                                                    )}
                                                </NeoGrid.Grid>
                                            </div>
                                        </Neo.Pager>
                                    </div>
                                </Neo.Card>
                            }
                            {this.viewModel.securityHoldingsPageManager.totalRecords > 0 &&
                                <Neo.Card title={TextConstants.Titles.SecurityHoldings}>
                                    <div>
                                        <Neo.Pager pageManager={this.viewModel.securityHoldingsPageManager} pageControlProps={{ pageSizeLabel: "Show: " }} >
                                            <div className="horizontalScroll inner-container">
                                                <NeoGrid.Grid<SecurityHoldingLookup> title={TextConstants.Titles.SecurityHoldings}>
                                                    {(item, meta) => (
                                                        <NeoGrid.Row>
                                                            <NeoGrid.Column label={TextConstants.Labels.ClientNumber} display={meta.clientAccount} />
                                                            <NeoGrid.Column label={TextConstants.Labels.Account} display={meta.rawAccountNumber} />
                                                            <NeoGrid.Column label={TextConstants.Labels.InstrumentDescription} display={meta.instrumentDescription} />
                                                            <NeoGrid.Column label={TextConstants.Labels.TickerSymbol} display={meta.tickerSymbol} />
                                                            <NeoGrid.Column label={TextConstants.Labels.Instrument} display={meta.instrument} />
                                                            <NeoGrid.Column label={TextConstants.Labels.Isin} display={meta.isin} />
                                                            <NeoGrid.Column label={TextConstants.Labels.ReportDate} display={meta.reportDate} />
                                                            <NeoGrid.Column label={TextConstants.Labels.Quantity} display={meta.quantity} cellClassName="text-right" />
                                                            <NeoGrid.Column label={TextConstants.Labels.InstrumentCurrency} display={meta.instrumentCurrency} />
                                                            <NeoGrid.Column label={TextConstants.Labels.MarketPrice} display={meta.priceFactor} cellClassName="text-right" />
                                                            <NeoGrid.Column label={TextConstants.Labels.Value} display={meta.value} cellClassName="text-right" />
                                                            <NeoGrid.Column label={TextConstants.Labels.ValueNC} display={meta.valueUSD} cellClassName="text-right" />
                                                            <NeoGrid.Column label={TextConstants.Labels.PortfolioBookPrice} display={meta.portfolioBookPriceTC} cellClassName="text-right" />
                                                        </NeoGrid.Row>
                                                    )}
                                                </NeoGrid.Grid>
                                            </div>
                                        </Neo.Pager>
                                    </div>
                                </Neo.Card>
                            }
                            {(this.viewModel.cashHoldingsPageManager.totalRecords === 0 || this.viewModel.securityHoldingsPageManager.totalRecords === 0) &&
                                <div className="col-md-12 p-0 text-center">
                                    {TextConstants.GeneralText.NoRecords}
                                </div>
                            }
                        </Neo.Card>
                    }
                </CollapsibleCard>
            </div>
        )
    }
}